<!--
  Import tab, ported from vendor-ref/mara/app/ui/src/components/nanome/WorkspaceImportFile.vue: the RCSB card and the
  Click to Upload card side by side, then Import from Chat (ChatRecentFiles.vue inline: title, search, Date sort,
  Name / Chat / Date header). RCSB and upload load locally; the chat file list needs a Nanome account, so it shows
  placeholder rows under a blur with the shared nudge.
-->
<script setup lang="ts">
import { inject, ref } from 'vue';
import { useFileDialog } from '@vueuse/core';
import Button from 'primevue/button';
import InputText from 'primevue/inputtext';
import Select from 'primevue/select';
import IconField from 'primevue/iconfield';
import InputIcon from 'primevue/inputicon';
import { ACCEPT_EXTS, useLiteStore } from '@nanome-lite/state';
import TeaserCta from './TeaserCta.vue';
import { OPEN_IN_NANOME } from './open-in-nanome-key';

const emit = defineEmits<{ imported: [] }>();

const store = useLiteStore();
const openInNanome = inject(OPEN_IN_NANOME, () => {});

const loadingFiles = ref(false);
const loadingRCSB = ref(false);
const pdbId = ref('');
const sortField = ref('date');
const sortOptions = [{ label: 'Name', value: 'name' }, { label: 'Date', value: 'date' }];

/** Decorative rows behind the blur. */
const placeholderRows = [
  { name: 'receptor_prepared.pdb', chat: 'Pocket analysis', date: '9/22/2026' },
  { name: 'docked_poses.sdf', chat: 'Docking run', date: '9/22/2026' },
  { name: 'ligand_series.sdf', chat: 'SAR review', date: '9/21/2026' },
  { name: 'esmfold_model.pdb', chat: 'Fold a sequence', date: '9/20/2026' },
  { name: 'minimized_complex.pdb', chat: 'Minimization', date: '9/19/2026' },
  { name: 'trajectory_frame.pdb', chat: 'MD analysis', date: '9/18/2026' },
];

const { open: openFileDialog, reset: resetFileDialog, onChange: onFileSelected } = useFileDialog({ accept: ACCEPT_EXTS, multiple: true });
onFileSelected(async (files) => {
  if (!files?.length) return;
  loadingFiles.value = true;
  try { await store.addEntryFromFiles(Array.from(files)); emit('imported'); }
  catch { /* toasted by the store */ }
  finally { loadingFiles.value = false; resetFileDialog(); }
});

async function importFromRCSB() {
  if (pdbId.value.length !== 4) return;
  loadingRCSB.value = true;
  try { await store.addEntryFromRcsb(pdbId.value); pdbId.value = ''; emit('imported'); }
  catch { /* toasted by the store */ }
  finally { loadingRCSB.value = false; }
}
</script>

<template>
  <div class="min-h-0 flex-1 flex flex-col gap-4" data-test="import-tab">
    <div class="flex gap-4">
      <form class="flex-1 flex flex-col gap-2 bg-content p-4 rd-3" data-test="import-rcsb" @submit.prevent="importFromRCSB">
        <div class="text-sm font-medium">
          Import from RCSB
        </div>
        <InputText v-model="pdbId" maxlength="4" class="w-full rd-full" placeholder="PDB ID" autofocus />
        <Button :disabled="pdbId.length !== 4" :loading="loadingRCSB" type="submit" label="Load" severity="primary" rounded />
      </form>

      <Button
        :disabled="loadingFiles"
        class="flex-1 flex flex-col gap-0 rd-3 b-0 bg-content hover:bg-section"
        severity="secondary"
        data-test="import-upload"
        @click="openFileDialog()"
      >
        <i :class="loadingFiles ? 'i-tabler-loader-2 animate-spin' : 'i-tabler-upload'" class="text-2xl mb-2" />
        <div class="text-sm">
          Click to Upload
        </div>
        <div class="text-xs op-50">
          Or drag and drop files
        </div>
      </Button>
    </div>

    <div class="-mt-2 px-1 text-xs op-60" data-test="import-nudge">
      Saved uploads, trajectories and DX maps live in your Nanome workspace.
      <a class="text-primary cursor-pointer ws-nowrap" @click="openInNanome()">Open in Nanome</a>
    </div>

    <div class="min-h-0 flex-1 flex flex-col gap-2 p-4 bg-content rd-3" data-test="import-chat">
      <div class="flex items-center justify-end gap-2">
        <div class="mr-auto text-sm font-medium truncate">
          Import from Chat
        </div>
        <Button icon="i-tabler-search" severity="secondary" size="small" rounded disabled aria-label="Search" />
        <IconField>
          <InputIcon class="i-tabler-arrows-sort" />
          <Select
            v-model="sortField"
            :options="sortOptions"
            option-label="label"
            option-value="value"
            class="rd-full"
            size="small"
            variant="filled"
            disabled
          />
        </IconField>
      </div>

      <div class="relative min-h-0 flex-1 flex flex-col of-hidden">
        <div class="grid grid-cols-[minmax(0,2fr)_minmax(0,1fr)_6rem_3rem] bg-content b-0 b-b b-solid b-section text-xs font-bold uppercase">
          <span class="px-3 py-2 op-50">Name</span>
          <span class="px-3 py-2 op-50">Chat</span>
          <span class="px-3 py-2 op-50">Date</span>
          <span />
        </div>
        <div class="min-h-0 flex-1 of-hidden select-none pointer-events-none blur-[3px] op-60" aria-hidden="true">
          <div
            v-for="row in placeholderRows"
            :key="row.name"
            class="h-11 grid grid-cols-[minmax(0,2fr)_minmax(0,1fr)_6rem_3rem] items-center"
          >
            <div class="flex items-center gap-2 min-w-0 px-3 py-2">
              <span class="w-5 h-5 shrink-0 rd-1 bg-ground" />
              <span class="text-sm truncate">{{ row.name }}</span>
            </div>
            <div class="flex items-center gap-1 min-w-0 px-3 text-xs op-50">
              <i class="i-tabler-messages shrink-0" />
              <span class="truncate">{{ row.chat }}</span>
            </div>
            <span class="px-3 text-xs op-50">{{ row.date }}</span>
            <i class="i-tabler-dots justify-self-center op-50" />
          </div>
        </div>
        <div class="absolute inset-0 top-9 flex items-center justify-center p-4">
          <TeaserCta title="Your MARA chat files appear here in Nanome" text="Import results from any chat into the workspace." card />
        </div>
      </div>
    </div>
  </div>
</template>
